import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Lightning, Crown, MapPin, Heart, CheckCircle, ArrowRight, Globe,
  Trophy, ShareNetwork, Code, Phone, NavigationArrow, Storefront,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { withUtm } from "../utils/utm";

const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
};

const LocalHeroMicrosite = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showEmbed, setShowEmbed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/local-hero/by-slug/${slug}`);
        setData(r.data);
      } catch (e) {
        setError(e.response?.status === 404 ? "This Local Hero microsite isn't public yet." : "Couldn't load this microsite.");
      } finally { setLoading(false); }
    })();
  }, [slug]);

  const shareUrl = `${window.location.origin}/local-hero/${slug}`;
  // Tagged URLs for outbound attribution. Share goes to social posts; embed lives on the sponsor's own site.
  const shareUrlTagged = withUtm(shareUrl, "share", "social", `local_hero_${slug}`);
  const embedUrlTagged = withUtm(shareUrl, "local_hero_badge", "embed", `local_hero_${slug}`);

  /** Log outbound clicks to the sponsoring business — powers the monthly sponsor impact report. */
  const trackClick = (clickType, destination) => {
    // Fire-and-forget; never block the user's navigation
    axios.post(`${API}/local-hero/track-click`, {
      slug,
      click_type: clickType,
      destination: destination || "",
    }).catch(() => {});
  };

  const normalizedWebsite = data?.profile?.website
    ? (data.profile.website.startsWith("http") ? data.profile.website : `https://${data.profile.website}`)
    : "";
  const phoneHref = data?.profile?.phone ? `tel:${data.profile.phone.replace(/[^\d+]/g, "")}` : "";
  const mapsHref = data?.profile?.address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(data.profile.address)}`
    : "";

  const copyShare = async () => {
    try {
      if (navigator.share) await navigator.share({ title: `${data?.profile?.business_name} — SC Local Hero`, url: shareUrlTagged });
      else { await navigator.clipboard.writeText(shareUrlTagged); toast.success("Link copied"); }
    } catch { /* user cancelled */ }
  };

  const embedSnippet = `<a href="${embedUrlTagged}" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:6px;padding:8px 12px;background:#10B981;color:#000;border-radius:4px;font-family:system-ui,sans-serif;font-weight:700;font-size:13px;text-decoration:none;">⚡ Proud SC Local Hero — FlashGrants</a>`;

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet);
      toast.success("Embed snippet copied — paste into your website HTML");
    } catch { toast.error("Couldn't copy"); }
  };

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center text-zinc-500">Loading…</div>;
  if (error) return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center text-center px-6">
      <Crown className="w-12 h-12 text-zinc-700 mb-3" />
      <p className="text-zinc-400 mb-4">{error}</p>
      <Link to="/local-hero" className="text-emerald-400 underline">Learn about Local Hero →</Link>
    </div>
  );

  const { profile, stats } = data;

  return (
    <div className="min-h-screen bg-black text-zinc-100" data-testid="local-hero-microsite">
      {/* Header */}
      <header className="border-b border-zinc-900">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Lightning weight="fill" className="w-6 h-6 text-emerald-500" />
            <span className="font-bold tracking-tight">FlashGrants</span>
          </Link>
          <div className="flex items-center gap-1.5">
            <Button onClick={copyShare} variant="outline" size="sm" className="border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-300 rounded-sm" data-testid="share-btn">
              <ShareNetwork className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Share</span>
            </Button>
            <Button onClick={() => setShowEmbed((s) => !s)} variant="outline" size="sm" className="border-zinc-800 hover:border-emerald-500/40 hover:text-emerald-300 rounded-sm" data-testid="embed-btn">
              <Code className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Badge</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-zinc-900">
        <div className="max-w-5xl mx-auto px-5 py-12">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-400 font-mono mb-4">
            <Crown weight="fill" className="w-3.5 h-3.5" /> Verified SC Local Hero
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-3" data-testid="business-name">{profile.business_name}</h1>
          {profile.tagline && <p className="text-lg text-zinc-300 mb-3">{profile.tagline}</p>}
          <div className="flex flex-wrap gap-3 text-sm text-zinc-500">
            {profile.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{profile.location}</span>}
            {profile.website && (
              <a href={profile.website.startsWith("http") ? profile.website : `https://${profile.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-emerald-400 hover:text-emerald-300" onClick={() => trackClick("website", profile.website)} data-testid="hero-website-link">
                <Globe className="w-4 h-4" />{profile.website.replace(/^https?:\/\//, "")}
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Visit This Business — Wardell's traffic-driving CTA section.
          The whole point of Local Hero: drive measurable, trackable traffic to the sponsor. */}
      {(normalizedWebsite || phoneHref || mapsHref) && (
        <section className="bg-gradient-to-br from-emerald-500/15 via-emerald-500/5 to-transparent border-b border-emerald-500/30" data-testid="visit-business-section">
          <div className="max-w-5xl mx-auto px-5 py-10">
            <div className="flex items-start gap-3 mb-5">
              <Storefront weight="duotone" className="w-8 h-8 text-emerald-400 flex-shrink-0 mt-1" />
              <div>
                <p className="text-xs uppercase tracking-widest text-emerald-400 font-mono mb-1">Support this Local Hero</p>
                <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
                  Visit {profile.business_name}
                </h2>
                <p className="text-sm text-zinc-400 mt-1.5">
                  Every visit, call, or order helps this business keep giving back to South Carolina.
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cta-grid">
              {normalizedWebsite && (
                <a
                  href={normalizedWebsite}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackClick("website", normalizedWebsite)}
                  className="group flex items-center justify-between gap-3 bg-emerald-500 text-black font-bold rounded-sm px-5 py-4 hover:bg-emerald-400 transition-colors"
                  data-testid="cta-website"
                >
                  <span className="flex items-center gap-2">
                    <Globe weight="bold" className="w-5 h-5" />
                    {profile.cta_label || "Visit Website"}
                  </span>
                  <ArrowRight weight="bold" className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              )}
              {phoneHref && (
                <a
                  href={phoneHref}
                  onClick={() => trackClick("phone", profile.phone)}
                  className="group flex items-center justify-between gap-3 bg-zinc-900 border border-zinc-700 text-white font-bold rounded-sm px-5 py-4 hover:border-emerald-500/60 hover:bg-zinc-800 transition-colors"
                  data-testid="cta-phone"
                >
                  <span className="flex items-center gap-2">
                    <Phone weight="fill" className="w-5 h-5 text-emerald-400" />
                    {profile.phone}
                  </span>
                  <span className="text-xs text-zinc-400 font-normal">Tap to call</span>
                </a>
              )}
              {mapsHref && (
                <a
                  href={mapsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackClick("directions", profile.address)}
                  className="group flex items-center justify-between gap-3 bg-zinc-900 border border-zinc-700 text-white font-bold rounded-sm px-5 py-4 hover:border-emerald-500/60 hover:bg-zinc-800 transition-colors"
                  data-testid="cta-directions"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <NavigationArrow weight="fill" className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="truncate">{profile.address}</span>
                  </span>
                  <span className="text-xs text-zinc-400 font-normal flex-shrink-0">Directions</span>
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Stats strip */}
      <section className="bg-emerald-500/5 border-b border-zinc-900">
        <div className="max-w-5xl mx-auto px-5 py-8 grid grid-cols-3 gap-4 text-center" data-testid="stats-strip">
          <div>
            <p className="text-3xl sm:text-4xl font-black text-emerald-400" data-testid="total-dollars">${stats.total_dollars.toLocaleString()}</p>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mt-1">Total given</p>
          </div>
          <div>
            <p className="text-3xl sm:text-4xl font-black text-zinc-100" data-testid="nonprofit-count">{stats.nonprofit_count}</p>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mt-1">SC nonprofits supported</p>
          </div>
          <div>
            <p className="text-3xl sm:text-4xl font-black text-zinc-100">{stats.log_count}</p>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mt-1">Verified contributions</p>
          </div>
        </div>
      </section>

      {/* Personal message */}
      {profile.custom_message && (
        <section className="border-b border-zinc-900">
          <div className="max-w-3xl mx-auto px-5 py-10">
            <p className="text-xs uppercase tracking-widest text-emerald-400 font-mono mb-2">From the team</p>
            <p className="text-lg sm:text-xl text-zinc-200 leading-relaxed italic">"{profile.custom_message}"</p>
          </div>
        </section>
      )}

      {/* Focus areas */}
      {profile.focus_areas?.length > 0 && (
        <section className="border-b border-zinc-900">
          <div className="max-w-5xl mx-auto px-5 py-10">
            <p className="text-xs uppercase tracking-widest text-emerald-400 font-mono mb-3">Causes we champion</p>
            <div className="flex flex-wrap gap-2">
              {profile.focus_areas.map((f) => (
                <span key={f} className="text-sm bg-zinc-900 border border-zinc-800 rounded-sm px-3 py-1.5 text-zinc-200">{f}</span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recent sponsorships */}
      <section className="border-b border-zinc-900">
        <div className="max-w-5xl mx-auto px-5 py-12">
          <h2 className="text-xs uppercase tracking-widest text-emerald-400 font-mono mb-3">Recent contributions</h2>
          {stats.recent?.length === 0 ? (
            <p className="text-sm text-zinc-500">No public contributions yet.</p>
          ) : (
            <div className="space-y-2" data-testid="recent-list">
              {stats.recent.map((r, i) => (
                <div key={i} className="bg-zinc-950 border border-zinc-800 rounded-sm p-4 flex flex-wrap items-center justify-between gap-3 hover:border-emerald-500/30 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-zinc-100">{r.nonprofit_name}</p>
                    {r.program_name && <p className="text-xs text-zinc-500 truncate">{r.program_name}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-400 font-bold font-mono">${r.amount.toLocaleString()}</p>
                    <p className="text-[10px] text-zinc-500 font-mono">{fmtDate(r.sponsorship_date)}</p>
                  </div>
                  {r.acknowledgement_received && <CheckCircle weight="fill" className="w-5 h-5 text-emerald-400" title="Acknowledged" />}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Embed snippet (toggled) */}
      {showEmbed && (
        <section className="border-b border-zinc-900 bg-zinc-950" data-testid="embed-section">
          <div className="max-w-3xl mx-auto px-5 py-8">
            <p className="text-xs uppercase tracking-widest text-emerald-400 font-mono mb-2">Embed your Local Hero badge</p>
            <p className="text-sm text-zinc-400 mb-3">Paste this HTML into your website footer, email signature, or About page:</p>
            <pre className="bg-black border border-zinc-800 rounded-sm p-3 text-[11px] text-zinc-300 overflow-x-auto whitespace-pre-wrap break-all" data-testid="embed-snippet">{embedSnippet}</pre>
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <Button onClick={copyEmbed} size="sm" className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm" data-testid="copy-embed-btn">
                Copy snippet
              </Button>
              <span className="text-xs text-zinc-500">Preview:</span>
              <a href={shareUrl} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 bg-emerald-500 text-black font-bold rounded-sm px-3 py-2 text-xs no-underline">
                ⚡ Proud SC Local Hero — FlashGrants
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Call to action — recruit other businesses */}
      <section className="border-b border-zinc-900">
        <div className="max-w-3xl mx-auto px-5 py-12 text-center">
          <Trophy weight="duotone" className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
          <h3 className="text-2xl font-bold mb-2">Want your business listed here too?</h3>
          <p className="text-sm text-zinc-400 mb-5 max-w-xl mx-auto">
            FlashGrants Local Hero turns your community giving into a verifiable annual partnership — audit-ready quarterly Impact PDFs, named recognition with SC nonprofits, and your own branded microsite like this one.
          </p>
          <Link to="/local-hero">
            <Button className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm px-6 py-5" data-testid="cta-learn-more">
              <Heart weight="fill" className="w-4 h-4 mr-2" />
              Become a SC Local Hero
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 text-center text-xs text-zinc-600 pb-20 sm:pb-6">
        Verified by <Link to="/" className="text-emerald-400 hover:text-emerald-300">FlashGrants</Link> ·
        All contributions self-reported by the business and cross-referenced with nonprofit acknowledgement letters
      </footer>

      {/* Sticky mobile CTA bar — keeps the primary action one-tap-away on small screens */}
      {(normalizedWebsite || phoneHref) && (
        <div className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-black/95 backdrop-blur border-t border-emerald-500/30 px-3 py-2.5 flex gap-2" data-testid="sticky-mobile-cta">
          {normalizedWebsite && (
            <a
              href={normalizedWebsite}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick("website", normalizedWebsite)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 bg-emerald-500 text-black font-bold rounded-sm px-3 py-3 text-sm"
              data-testid="sticky-cta-website"
            >
              <Globe weight="bold" className="w-4 h-4" />
              {profile.cta_label || "Visit"}
            </a>
          )}
          {phoneHref && (
            <a
              href={phoneHref}
              onClick={() => trackClick("phone", profile.phone)}
              className="flex-1 inline-flex items-center justify-center gap-1.5 bg-zinc-900 border border-zinc-700 text-white font-bold rounded-sm px-3 py-3 text-sm"
              data-testid="sticky-cta-phone"
            >
              <Phone weight="fill" className="w-4 h-4 text-emerald-400" />
              Call
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default LocalHeroMicrosite;
